import { useState, useCallback, useRef, type FormEvent, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { User, Lock, Building2, Globe, Upload, X } from 'lucide-react';

import { useAuthStore } from '@/store/authStore';
import { useUpdateProfile } from '@/api/hooks/useAuth';
import * as authService from '@/api/services/auth.service';
import i18n, { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/i18n/i18n';

export function SettingsPage() {
  const { t } = useTranslation(['settings', 'common']);

  return (
    <div className="page-container">
      <h1 className="page-title">{t('settings:title')}</h1>
      <div className="mt-6 max-w-2xl space-y-8">
        <ProfileSection />
        <PasswordSection />
        <StudioSection />
        <PreferencesSection />
      </div>
    </div>
  );
}

/* ================================================================
   Profile Section
   ================================================================ */
function ProfileSection() {
  const { t } = useTranslation(['settings', 'common']);
  const user = useAuthStore((s) => s.user);
  const updateProfile = useUpdateProfile();

  const [firstName, setFirstName] = useState(user?.first_name ?? '');
  const [lastName, setLastName] = useState(user?.last_name ?? '');
  const [preferredLanguage, setPreferredLanguage] = useState<SupportedLanguage>(
    (user?.preferred_language as SupportedLanguage) ?? 'en',
  );

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      updateProfile.mutate({
        first_name: firstName,
        last_name: lastName,
        preferred_language: preferredLanguage,
      });
    },
    [firstName, lastName, preferredLanguage, updateProfile],
  );

  return (
    <div className="card">
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
          <User className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">
            {t('settings:profile.title')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('settings:profile.description')}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings:profile.firstName')}
            </label>
            <input
              type="text"
              className="input-field"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings:profile.lastName')}
            </label>
            <input
              type="text"
              className="input-field"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('settings:profile.email')}
          </label>
          <input
            type="email"
            className="input-field"
            value={user?.email ?? ''}
            disabled
          />
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            {t('settings:profile.emailReadonly')}
          </p>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('settings:profile.preferredLanguage')}
          </label>
          <select
            className="input-field"
            value={preferredLanguage}
            onChange={(e) => setPreferredLanguage(e.target.value as SupportedLanguage)}
          >
            {SUPPORTED_LANGUAGES.map((lng) => (
              <option key={lng} value={lng}>
                {t(`common:language.${lng}`)}
              </option>
            ))}
          </select>
        </div>

        <div className="pt-2">
          <button
            type="submit"
            className="btn-primary"
            disabled={updateProfile.isPending}
          >
            {updateProfile.isPending ? t('common:status.saving') : t('settings:profile.saveProfile')}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ================================================================
   Password Section
   ================================================================ */
function PasswordSection() {
  const { t } = useTranslation(['settings', 'common']);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = useCallback((): boolean => {
    const errs: Record<string, string> = {};
    if (!currentPassword) errs.current = t('settings:password.validation.currentRequired');
    if (!newPassword) errs.new = t('settings:password.validation.newRequired');
    else if (newPassword.length < 8) errs.new = t('settings:password.validation.newMin');
    if (!confirmPassword) errs.confirm = t('settings:password.validation.confirmRequired');
    else if (newPassword !== confirmPassword) errs.confirm = t('settings:password.validation.mismatch');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [currentPassword, newPassword, confirmPassword, t]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!validate()) return;

      setIsSubmitting(true);
      try {
        await authService.changePassword(currentPassword, newPassword);
        toast.success(t('settings:password.changed'));
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setErrors({});
      } catch {
        toast.error(t('settings:password.changeError'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [currentPassword, newPassword, validate, t],
  );

  return (
    <div className="card">
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400">
          <Lock className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">
            {t('settings:password.title')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('settings:password.description')}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('settings:password.currentPassword')}
          </label>
          <input
            type="password"
            className="input-field"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
          />
          {errors.current && (
            <p className="mt-1 text-xs text-red-500">{errors.current}</p>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings:password.newPassword')}
            </label>
            <input
              type="password"
              className="input-field"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
            />
            {errors.new && (
              <p className="mt-1 text-xs text-red-500">{errors.new}</p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings:password.confirmNewPassword')}
            </label>
            <input
              type="password"
              className="input-field"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
            {errors.confirm && (
              <p className="mt-1 text-xs text-red-500">{errors.confirm}</p>
            )}
          </div>
        </div>

        <div className="pt-2">
          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? t('common:status.saving') : t('settings:password.changePassword')}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ================================================================
   Studio Section
   ================================================================ */
function StudioSection() {
  const { t } = useTranslation(['settings', 'common']);
  const [studioName, setStudioName] = useState('');
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLogoChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  const removeLogo = useCallback(() => {
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      // Studio name could be stored in user profile or a separate tenant model.
      // For now we show the UI; a full implementation would call a studio API.
      toast.success(t('settings:saved'));
    },
    [t],
  );

  return (
    <div className="card">
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400">
          <Building2 className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">
            {t('settings:studio.title')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('settings:studio.description')}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('settings:studio.studioName')}
          </label>
          <input
            type="text"
            className="input-field"
            placeholder={t('settings:studio.studioNamePlaceholder')}
            value={studioName}
            onChange={(e) => setStudioName(e.target.value)}
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('settings:studio.logo')}
          </label>

          {logoPreview ? (
            <div className="relative inline-block">
              <img
                src={logoPreview}
                alt="Studio logo"
                className="h-24 w-24 rounded-lg border border-gray-200 object-cover dark:border-gray-700"
              />
              <button
                type="button"
                onClick={removeLogo}
                className="absolute -end-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white shadow-sm hover:bg-red-600"
                aria-label={t('settings:studio.removeLogo')}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-300 text-gray-400 transition-colors hover:border-primary-400 hover:text-primary-500 dark:border-gray-600 dark:hover:border-primary-500"
            >
              <Upload className="h-6 w-6" />
              <span className="text-xs">{t('settings:studio.uploadLogo')}</span>
            </button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/svg+xml,image/jpeg"
            className="hidden"
            onChange={handleLogoChange}
          />
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            {t('settings:studio.logoHint')}
          </p>
        </div>

        <div className="pt-2">
          <button type="submit" className="btn-primary">
            {t('settings:studio.saveStudio')}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ================================================================
   Preferences Section (Language + Theme with live switching)
   ================================================================ */
function PreferencesSection() {
  const { t } = useTranslation(['settings', 'common']);

  const handleLanguageChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    const lng = e.target.value;
    i18n.changeLanguage(lng);
  }, []);

  const handleThemeChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    const theme = e.target.value;
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else if (theme === 'light') {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    } else {
      // System
      localStorage.removeItem('theme');
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.classList.toggle('dark', prefersDark);
    }
  }, []);

  const currentTheme = (() => {
    const stored = localStorage.getItem('theme');
    if (stored === 'dark' || stored === 'light') return stored;
    return 'system';
  })();

  return (
    <div className="card">
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400">
          <Globe className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">
            {t('settings:preferences.title')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('settings:preferences.description')}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('settings:preferences.language')}
          </label>
          <select
            className="input-field"
            value={i18n.language}
            onChange={handleLanguageChange}
          >
            {SUPPORTED_LANGUAGES.map((lng) => (
              <option key={lng} value={lng}>
                {t(`common:language.${lng}`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('settings:preferences.theme')}
          </label>
          <select
            className="input-field"
            defaultValue={currentTheme}
            onChange={handleThemeChange}
          >
            <option value="light">{t('settings:preferences.themeLight')}</option>
            <option value="dark">{t('settings:preferences.themeDark')}</option>
            <option value="system">{t('settings:preferences.themeSystem')}</option>
          </select>
        </div>
      </div>
    </div>
  );
}
